<template>
  <div class="current-commission-transactions">
    <div class="table-responsive mt-3">
      <h5>Commission Transactions ({{ transactions.length }})</h5>

      <form @submit.prevent="saveTransaction(editingTransaction ? 'edit' : 'add')">
        <div class="form-row mx-0">
          <div class="form-group col-md-4 pl-0">
            <label for="amount">Amount</label>
            <input type="number" step="0.01" class="form-control" v-model="newTransaction.amount" placeholder="Amount">
          </div>
          <div class="form-group col-md-5">
            <label for="description">Description</label>
            <input type="text" class="form-control" v-model="newTransaction.description" placeholder="Description">
          </div>
          <div class="form-group col-md-3 pr-0">
            <button type="submit" class="btn btn-success btn-add-transaction text-nowrap w-100">
              {{ editingTransaction ? 'Update Transaction' : 'Add Transaction' }}
            </button>
            <button v-if="editingTransaction" @click="cancelEdit" type="button" class="btn btn-secondary text-nowrap w-100 mt-2">
              Cancel
            </button>
          </div>
        </div>
      </form>

      <p v-if="transactions.length === 0">
        No transactions found. Please add a transaction.
      </p>

      <table v-else class="table table-bordered">
        <thead>
          <tr>
            <th>Amount</th>
            <th>Description</th>
            <th>Status</th>
            <th>Processed At</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(transaction, index) in transactions" :key="'tx-' + index">
            <td>{{ transaction.amount }}</td>
            <td>{{ transaction.description }}</td>
            <td>{{ transaction.status || '-' }}</td>
            <td>{{ transaction.processed_at || '-' }}</td>
            <td>
              <button @click="editTransaction(transaction)" class="btn btn-warning btn-sm">Edit</button>
              <button @click="removeTransaction(transaction.id)" class="btn btn-danger btn-sm">Remove</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { addTransaction, deleteTransaction, updateTransaction } from "../../../services/api/commissions";
import '../../../../sass/commissions/current-commissions.scss';

export default {
  name: 'CurrentCommissionTransactions',
  props: {
    commissionId: {
      type: Number,
      default: 0
    },
    comTransactions: {
      type: [Array, Object],
      default: () => []
    }
  },
  data() {
    return {
      isLoading: false,
      transactions: [],
      editingTransaction: null,
      newTransaction: {
        amount: "",
        description: ""
      }
    }
  },
  mounted() {
    this.initTransactions(this.comTransactions);
  },
  watch: {
    comTransactions: {
      handler(value) {
        this.initTransactions(value);
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    initTransactions(transactions) {
      this.transactions = transactions.map(transaction => ({
        id: transaction.id,
        amount: transaction.amount,
        description: transaction.description || '-',
        status: transaction.status || '-',
        processed_at: transaction.processed_at || '-'
      }));
    },
    validateTransaction(transaction) {
      return transaction.amount && transaction.description;
    },
    async saveTransaction(type = 'add') {
      try {
        let response;
        this.isLoading = true;

        if (!this.validateTransaction(this.newTransaction)) {
          new Noty({
            type: "warning",
            text: 'Please fill in all fields before saving the transaction.',
          }).show();
          return;
        }

        const payload = {
          amount: this.newTransaction.amount,
          description: this.newTransaction.description,
        };

        if (type === 'add') {
          response = await addTransaction(this.commissionId, payload);
        } else if (type === 'edit') {
          response = await updateTransaction(this.editingTransaction.id, payload);
        }

        if (response.status === 200 || response.status === 201) {
          new Noty({
            type: "success",
            text: type === 'add' ? 'Transaction added successfully' : 'Transaction updated successfully',
          }).show();

          if (type === 'add') {
            this.transactions.push({
              ...this.newTransaction,
              status: 'Pending',
              processed_at: '-'
            });
          } else {
            const index = this.transactions.findIndex(tx => tx.id === this.editingTransaction.id);
            this.$set(this.transactions, index, { ...this.editingTransaction, ...this.newTransaction });
          }

          this.resetNewTransaction();
          this.editingTransaction = null;
        } else {
          new Noty({
            type: "error",
            text: 'Failed to save transaction. Please try again later.',
          }).show();
        }

      } catch (error) {
        console.error('Error saving transaction:', error);

        new Noty({
          type: "error",
          text: 'Failed to save transaction. Please try again later.',
        }).show();
      } finally {
        this.isLoading = false;
      }
    },
    async removeTransaction(transactionId) {
      try {
        this.isLoading = true;
        const response = await deleteTransaction(transactionId);

        if (response.status === 200) {
          new Noty({
            type: "success",
            text: 'Transaction removed successfully',
          }).show();

          this.transactions = this.transactions.filter(tx => tx.id !== transactionId);
        } else {
          new Noty({
            type: "error",
            text: 'Failed to remove transaction. Please try again later.',
          }).show();
        }
      } catch (error) {
        console.error('Error removing transaction:', error);

        new Noty({
          type: "error",
          text: 'Failed to remove transaction. Please try again later.',
        }).show();
      } finally {
        this.isLoading = false;
      }
    },
    editTransaction(transaction) {
      this.editingTransaction = transaction;
      this.newTransaction = { ...transaction };
    },
    cancelEdit() {
      this.editingTransaction = null;
      this.resetNewTransaction();
    },
    resetNewTransaction() {
      this.newTransaction = {
        amount: "",
        description: ""
      };
    }
  }
}
</script>